import { useQuery } from '@apollo/client';
import { Box } from '@mui/material';
import type { GridColDef, GridSortModel } from '@mui/x-data-grid-pro';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ListPagination } from '~/components/ListPagination';
import {
  ListActions,
  ListContainer,
  ListContent,
  ListHeader,
  ListPageContainer,
} from '~/components/PageLayout';
import { Count } from '~/components/count';
import { ScrollableDataGrid } from '~/components/data-grid';
import { EmptyState } from '~/components/empty-state';
import { SearchBar } from '~/components/forms/search-bar';
import { AuthorizedLink } from '~/components/links/AuthorizedLink';
import { useAppContext } from '~/contexts';
import type { PlaylistShowsOrderBy } from '~/generated/graphql';
import { useLink } from '~/hooks/link';
import { useOrderByParams } from '~/hooks/order-by';
import { usePaginationParams, useTotalCount } from '~/hooks/pagination';
import CreateShowIllustration from '~/images/illustrations/create-show.svg';
import {
  PlaylistUsageDocument,
  type PlaylistUsage__Show as Show,
} from '../queries/PlaylistUsage.generated';
import { Layout } from './components/layout';
import { PlaylistContainer, usePlaylist } from './context';

const ShowFolderLink = ({ show }: { show: Show }) => (
  <AuthorizedLink
    authorized={show.showFolder.canUpdate.value}
    entity={{ __typename: 'ShowFolder', id: show.showFolder.id, name: show.showFolder.name }}
  />
);

const useColumns = (): GridColDef<Show>[] => {
  const { t } = useTranslation();

  return [
    {
      field: 'name',
      flex: 2,
      minWidth: 180,
      headerName: t('pages.playlists.usage.show_column'),
      resizable: true,
      renderCell: ({ row }) => (
        <AuthorizedLink
          authorized={row.canUpdate.value}
          entity={{
            __typename: 'Show',
            id: row.id,
            name: row.name,
            showFolderId: row.showFolder.id,
          }}
        />
      ),
    },
    {
      field: 'showFolder',
      flex: 1,
      minWidth: 150,
      headerName: t('pages.playlists.usage.folder_column'),
      resizable: true,
      renderCell: ({ row }) => <ShowFolderLink show={row} />,
    },
  ];
};

const NoRowsOverlay = () => {
  const { t } = useTranslation();

  return (
    <EmptyState
      illustration={CreateShowIllustration}
      header={t('pages.playlists.usage.no_shows_header')}
      description={t('pages.playlists.usage.no_shows_description')}
    />
  );
};

const PlaylistUsageContent = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const { playlist } = usePlaylist();
  const { handleOutOfBounds, page, perPage } = usePaginationParams();
  const columns = useColumns();
  const navigate = useNavigate();
  const link = useLink();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';
  const { orderBy, setOrderByParams, sortModel } = useOrderByParams<PlaylistShowsOrderBy>();

  const onSortModelChange = useCallback(
    (model: GridSortModel) => {
      setOrderByParams(model.at(0) ?? null);
    },
    [setOrderByParams],
  );

  const { data, loading } = useQuery(PlaylistUsageDocument, {
    variables: {
      networkId: currentNetwork.id,
      playlistId: playlist.id,
      page,
      perPage,
      search,
      ...(orderBy && { orderBy }),
    },
    onCompleted: (d) => handleOutOfBounds(d.network?.playlist?.shows.pageInfo),
  });

  const shows = data?.network?.playlist?.shows.nodes ?? [];
  const totalCount = useTotalCount(data?.network?.playlist?.shows.pageInfo.nodesCount);

  return (
    <ListPageContainer>
      <ListContainer>
        <ListHeader>
          <ListActions>
            <SearchBar
              onChange={(value) =>
                setSearchParams((params) => {
                  params.set('search', value);
                  params.set('page', '1');
                  return params;
                })
              }
              placeholder={t('pages.playlists.usage.search_placeholder')}
              search={search}
            />
            <Count selectedCount={0} totalCount={totalCount} thing="common.pluralize.show_plural" />
          </ListActions>
        </ListHeader>
        <ListContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              <ScrollableDataGrid
                columns={columns}
                loading={loading}
                onRowClick={({ row }) =>
                  navigate(link(`/shows/${row.showFolder.id}/show/${row.id}/design`))
                }
                onSortModelChange={onSortModelChange}
                rows={shows}
                slots={{ columnResizeIcon: () => null, noRowsOverlay: NoRowsOverlay }}
                sortingMode="server"
                sortModel={sortModel}
              />
            </Box>
            <Box sx={{ flexShrink: 0 }}>
              <ListPagination totalCount={totalCount} />
            </Box>
          </Box>
        </ListContent>
      </ListContainer>
    </ListPageContainer>
  );
};

export const PlaylistUsage = () => (
  <PlaylistContainer>
    <Layout noPadding>
      <PlaylistUsageContent />
    </Layout>
  </PlaylistContainer>
);
